import { useState } from 'react';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

// V51 — SoulAI pregnancy check-in shown before any trial batch (free / trial learners).
export function TrialAssessment({ assessment, onDone }) {
  const [flags, setFlags] = useState([]);
  const [weeks, setWeeks] = useState(assessment?.weeks_pregnant || '');
  const [none, setNone] = useState(false);
  const [busy, setBusy] = useState(false);

  const toggle = (k) => { setNone(false); setFlags((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k])); };

  const submit = async () => {
    if (!weeks) { toast.error('Please tell us how many weeks pregnant you are'); return; }
    if (!none && flags.length === 0) { toast.error('Tick what applies, or choose "None of these"'); return; }
    setBusy(true);
    try {
      const r = await api.post('/api/trial/assessment', { flags: none ? [] : flags, weeks_pregnant: Number(weeks) });
      toast.success(r.data.message);
      onDone(r.data);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error((typeof d === 'object' ? d?.message : d) || 'Could not save your answers');
    }
    setBusy(false);
  };

  return (
    <Card className="border-emerald-200" data-testid="trial-assessment-card">
      <CardContent className="p-5 sm:p-6 space-y-5">
        <div className="flex items-start gap-3">
          <Sparkles className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
          <div>
            <h2 className="text-base md:text-lg font-semibold">{assessment?.title || 'A quick check-in with SoulAI'}</h2>
            <p className="text-sm text-muted-foreground mt-0.5">{assessment?.intro || 'Tell us a little about your pregnancy so we show the right class for you.'}</p>
          </div>
        </div>
        <div className="max-w-xs">
          <label className="text-sm font-medium" htmlFor="trial-weeks">How many weeks pregnant are you?</label>
          <Input id="trial-weeks" type="number" min={1} max={45} value={weeks} onChange={(e) => setWeeks(e.target.value)} placeholder="e.g. 18" className="mt-1" data-testid="trial-assessment-weeks" />
        </div>
        <div>
          <p className="text-sm font-medium mb-2">Has your doctor mentioned any of these?</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {(assessment?.flags || []).map((f) => (
              <label key={f.key} className="flex items-start gap-2 rounded-lg border p-2.5 text-sm cursor-pointer hover:bg-muted/40" data-testid={`trial-flag-${f.key}`}>
                <Checkbox checked={flags.includes(f.key)} onCheckedChange={() => toggle(f.key)} className="mt-0.5" />
                <span>{f.label}</span>
              </label>
            ))}
            <label className="flex items-start gap-2 rounded-lg border p-2.5 text-sm cursor-pointer hover:bg-muted/40 sm:col-span-2" data-testid="trial-flag-none">
              <Checkbox checked={none} onCheckedChange={(v) => { setNone(!!v); if (v) setFlags([]); }} className="mt-0.5" />
              <span>None of these — my pregnancy has been smooth so far</span>
            </label>
          </div>
        </div>
        <Button onClick={submit} disabled={busy} data-testid="trial-assessment-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Show my classes'}
        </Button>
      </CardContent>
    </Card>
  );
}
